/**
 * useInvitationsList — TanStack Query hook over
 * `GET /organizations/:slug/invitations`.
 *
 * Mirrors `useUsersList`: returns a tagged-union result so the page
 * can branch loading / ready / forbidden / error without leaning on
 * a generic Retry affordance for the permission-denied case.
 *
 * Status surface (closed):
 *   - 'loading'   — request in flight, OR `useCurrentOrg` has not yet
 *                   resolved a membership (the hook short-circuits
 *                   `enabled` so no empty-slug request hits the wire).
 *   - 'ready'     — 200 OK; `data`, `total` populated.
 *   - 'forbidden' — 403; deliberate distinct branch so the page can
 *                   render a permission-denied panel rather than the
 *                   generic Retry affordance. The caller must already
 *                   have `identity.invitations.read` to land on the
 *                   route, so a 403 here means BE permission state
 *                   diverged from the cached `/auth/me` payload.
 *   - 'error'     — anything else (5xx, network, schema drift).
 *
 * Query-key uses the shared `buildInvitationsListQueryKey(orgSlug)`
 * prefix from `use-create-invitation.ts` so all invitations-related
 * mutations invalidate it with one call.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import { buildInvitationsListQueryKey } from './use-create-invitation.js';

export type InvitationStatus = 'pending' | 'accepted' | 'revoked' | 'expired';

export interface InvitationRow {
  readonly id: string;
  readonly email: string;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly role: { slug: string; displayName: string };
  readonly status: InvitationStatus;
  readonly expiresAt: string;
  readonly acceptedAt: string | null;
  readonly revokedAt: string | null;
  readonly createdAt: string;
  readonly invitedByUserId: string;
}

export interface InvitationsListInput {
  readonly status?: InvitationStatus;
  /** 1-indexed page number. BE rejects page < 1. */
  readonly page: number;
  /** BE caps at 100; admin portal defaults to 50. */
  readonly perPage: number;
}

export type UseInvitationsListResult =
  | {
      readonly status: 'loading';
      readonly data?: undefined;
      readonly total?: undefined;
      readonly page: number;
      readonly perPage: number;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'ready';
      readonly data: readonly InvitationRow[];
      readonly total: number;
      readonly page: number;
      readonly perPage: number;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'forbidden';
      readonly data?: undefined;
      readonly total?: undefined;
      readonly page: number;
      readonly perPage: number;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'error';
      readonly data?: undefined;
      readonly total?: undefined;
      readonly page: number;
      readonly perPage: number;
      readonly refetch: () => void;
    };

const FORBIDDEN = Symbol('invitations-list:forbidden');

interface InvitationsListEnvelope {
  readonly data: readonly InvitationDto[];
  readonly page: number;
  readonly per_page: number;
  readonly total: number;
}

interface InvitationDto {
  readonly id: string;
  readonly email: string;
  readonly first_name: string | null;
  readonly last_name: string | null;
  readonly role: { slug: string; display_name: string };
  readonly status: InvitationStatus;
  readonly expires_at: string;
  readonly accepted_at: string | null;
  readonly revoked_at: string | null;
  readonly created_at: string;
  readonly invited_by_user_id: string;
}

function projectRow(dto: InvitationDto): InvitationRow {
  return {
    id: dto.id,
    email: dto.email,
    firstName: dto.first_name,
    lastName: dto.last_name,
    role: {
      slug: dto.role.slug,
      displayName: dto.role.display_name,
    },
    status: dto.status,
    expiresAt: dto.expires_at,
    acceptedAt: dto.accepted_at,
    revokedAt: dto.revoked_at,
    createdAt: dto.created_at,
    invitedByUserId: dto.invited_by_user_id,
  };
}

function buildQueryParams(
  input: InvitationsListInput,
): Record<string, string | number> {
  const out: Record<string, string | number> = {
    page: input.page,
    per_page: input.perPage,
  };
  if (input.status !== undefined) {
    out.status = input.status;
  }
  return out;
}

export function useInvitationsList(
  input: InvitationsListInput,
): UseInvitationsListResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;

  const query = useQuery({
    queryKey: [
      ...buildInvitationsListQueryKey(orgSlug),
      input.status ?? 'any',
      input.page,
      input.perPage,
    ],
    enabled: orgSlug !== null,
    retry: false,
    // Retain prior page data through filter/page changes so the table
    // doesn't flash into 'loading' on every state change.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const result = await apiClient.GET(
        '/organizations/{slug}/invitations',
        {
          params: {
            path: { slug: orgSlug as string },
            query: buildQueryParams(input) as never,
          },
        },
      );
      if (result.response.status === 403) {
        return FORBIDDEN;
      }
      if (!result.response.ok || result.data === undefined) {
        throw new Error(
          `GET /organizations/${orgSlug}/invitations failed with status ${result.response.status}`,
        );
      }
      return result.data as InvitationsListEnvelope;
    },
  });

  const refetch = (): void => {
    void query.refetch();
  };

  if (orgSlug === null || query.isPending) {
    return {
      status: 'loading',
      page: input.page,
      perPage: input.perPage,
      refetch,
    };
  }
  if (query.isError) {
    return {
      status: 'error',
      page: input.page,
      perPage: input.perPage,
      refetch,
    };
  }
  if (query.data === FORBIDDEN) {
    return {
      status: 'forbidden',
      page: input.page,
      perPage: input.perPage,
      refetch,
    };
  }
  const envelope = query.data;
  return {
    status: 'ready',
    data: envelope.data.map(projectRow),
    total: envelope.total,
    page: envelope.page,
    perPage: envelope.per_page,
    refetch,
  };
}
